import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import plansData from '../assets/data/weeklyPlans.json';
import jsPDF from 'jspdf';

function DailyPlan() {
  const { week, day } = useParams();
  const plan = plansData[week]?.[day];
  const [selectedImage, setSelectedImage] = useState(null);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`${week.toUpperCase()} - ${day}`, 20, 20);

    if (plan) {
      let yPosition = 40;
      for (const [activity, description] of Object.entries(plan)) {
        if (activity !== 'videos' && activity !== 'images') {
          doc.setFontSize(12);
          doc.text(`${activity}: ${description}`, 20, yPosition);
          yPosition += 10;
        }
      }

      if (Array.isArray(plan.videos)) {
        plan.videos.forEach((video, index) => {
          doc.text(`Video ${index + 1}: ${video}`, 20, yPosition);
          yPosition += 10;
        });
      }
    } else {
      doc.text("No plan available for this date.", 20, 40);
    }

    doc.save(`${week}-${day}-plan.pdf`);
  };

  const getYouTubeEmbedUrl = (url) => {
    const regex = /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regex);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const styles = {
    container: {
      fontFamily: '"Helvetica Neue", Arial, sans-serif',
      padding: '20px',
      paddingTop: '170px',
      position: 'relative',
    },
    heading: {
      fontSize: '26px',
      fontWeight: 'bold',
      marginBottom: '15px',
      color: '#333',
      textAlign: 'center',
    },
    subheading: {
      fontSize: '20px',
      marginBottom: '10px',
      color: '#555',
    },
    list: {
      fontSize: '18px',
      listStyleType: 'none',
      padding: 0,
      marginBottom: '20px',
      color: '#444',
    },
    listItem: {
      marginBottom: '12px',
      lineHeight: '1.6',
    },
    imageContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '20px',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    image: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      maxHeight: '300px',
    },
    videoContainer: {
      position: 'relative',
      paddingBottom: '56.25%',
      paddingTop: '25px',
      height: 0,
      marginBottom: '20px',
      border: '3px solid #ccc',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
    iframe: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: '8px',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0 20px',
      marginBottom: '20px',
    },
    button: {
      padding: '10px 20px',
      fontSize: '16px',
      backgroundColor: '#ADD8E6',
      color: '#333',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
    },
    image: {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      maxHeight: '300px',
      cursor: 'pointer',
      transition: 'transform 0.2s ease-in-out',
      ':hover': {
        transform: 'scale(1.02)',
      },
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      maxWidth: '90%',
      maxHeight: '90%',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    modalImage: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
    },
  };

  return (
    <div style={styles.container}>
      {plan && (
        <div style={styles.buttonContainer}>
          <button style={styles.button} onClick={handlePrint}>Print</button>
          <button style={styles.button} onClick={handleDownloadPDF}>Download as PDF</button>
        </div>
      )}
      <h2 style={styles.heading}>
        {week.toUpperCase()} - {day}
      </h2>
      {plan ? (
        <div>
          <ul style={styles.list}>
            {Object.entries(plan).map(([activity, description]) => {
              if (activity === 'videos' || activity === 'images') return null;
              return (
                <li key={activity} style={styles.listItem}>
                  <strong>{activity}:</strong> {description}
                </li>
              );
            })}
          </ul>

          {Array.isArray(plan.videos) && plan.videos.length > 0 && (
            <div>
              <h3 style={styles.subheading}>Related Videos:</h3>
              {plan.videos.map((videoUrl, index) => {
                const embedUrl = getYouTubeEmbedUrl(videoUrl);
                return (
                  embedUrl && (
                    <div
                      key={index}
                      style={{
                        maxWidth: '640px',
                        margin: '0 auto 30px auto',
                        width: '100%',
                        aspectRatio: '16/9',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <iframe
                        src={embedUrl}
                        title={`YouTube video ${index + 1}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          width: '100%',
                          height: '100%',
                          border: '0',
                        }}
                      ></iframe>
                    </div>
                  )
                );
              })}
            </div>
          )}

          {Array.isArray(plan.images) && plan.images.length > 0 && (
            <div>
              <h3 style={styles.subheading}>Related Images:</h3>
              <div style={styles.imageContainer}>
                {plan.images.map((imageUrl, index) => (
                  <img
                    key={index}
                    src={imageUrl}
                    alt={`Activity image ${index + 1}`}
                    style={styles.image}
                    onClick={() => setSelectedImage(imageUrl)}
                  />
                ))}
              </div>
            </div>
          )}

          {selectedImage && (
            <div style={styles.modalOverlay} onClick={() => setSelectedImage(null)}>
              <div style={styles.modalContent}>
                <img
                  src={selectedImage}
                  alt="Enlarged view"
                  style={styles.modalImage}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

        </div>
      ) : (
        <p>{`No plan available for ${week}.`}</p>
      )}
    </div>
  );
}

export default DailyPlan;